import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { getDefaultTimeZoneOnBrowser, timestampToDate } from '../../util/dates';
import { LISTING_STATE_DRAFT, DATE_TYPE_DATETIME, propTypes } from '../../util/types';
import {
  Button,
  ButtonTabNavHorizontal,
  IconClose,
  IconEdit,
  IconSpinner,
  InlineTextButton,
  ListingLink,
  Modal,
  NamedLink,
  TimeRange,
  UpdatedInvoice,
} from '../../components';
import { EditListingAvailabilityPlanForm, EditListingAvailabilityExceptionForm } from '../../forms';
import { UnpaidBreakOptions, listingScheduleType } from '../../marketplace-custom-config';
import css from './EditListingAvailabilityPanel.module.css';
import HelpAndContact from '../HelpAndContact/HelpAndContact';
import EditListingSchedulingForm from '../../forms/EditListingSchedulingForm/EditListingSchedulingForm';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { createSlug, parse } from '../../util/urlHelpers';
import EditListingSchedulingRecurringForm from '../../forms/EditListingSchedulingForm/EditListingSchedulingRecurringForm';
import moment from 'moment';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api';
import { ReactComponent as InfoIcon } from '../../assets/svg/questionTooltip.svg';

import { Tooltip as ReactTooltip } from 'react-tooltip';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// We want to sort exceptions on the client-side, maximum pagination page size is 100,
// so we need to restrict the amount of exceptions to that.
const MAX_EXCEPTIONS_COUNT = 100;

const defaultTimeZone = () =>
  typeof window !== 'undefined' ? getDefaultTimeZoneOnBrowser() : 'Etc/UTC';

/////////////
// Weekday //
/////////////
const findEntry = (availabilityPlan, dayOfWeek) =>
  availabilityPlan.entries.find(d => d.dayOfWeek === dayOfWeek);

const getEntries = (availabilityPlan, dayOfWeek) =>
  availabilityPlan.entries.filter(d => d.dayOfWeek === dayOfWeek);

const Weekday = props => {
  const { availabilityPlan, dayOfWeek, openEditModal } = props;
  const hasEntry = findEntry(availabilityPlan, dayOfWeek);

  return (
    <div
      className={classNames(css.weekDay, { [css.blockedWeekDay]: !hasEntry })}
      onClick={() => openEditModal(true)}
      role="button"
    >
      <div className={css.dayOfWeek}>
        <FormattedMessage id={`EditListingAvailabilityPanel.dayOfWeek.${dayOfWeek}`} />
      </div>
      <div className={css.entries}>
        {availabilityPlan && hasEntry
          ? getEntries(availabilityPlan, dayOfWeek).map(e => {
            return (
              <span className={css.entry} key={`${e.dayOfWeek}${e.startTime}`}>{`${e.startTime
                } - ${e.endTime === '00:00' ? '24:00' : e.endTime}`}</span>
            );
          })
          : null}
      </div>
    </div>
  );
};

///////////////////////////////////////////////////
// EditListingAvailabilityExceptionPanel - utils //
///////////////////////////////////////////////////

// Create initial entry mapping for form's initial values
const createEntryDayGroups = (entries = {}) =>
  entries.reduce((groupedEntries, entry) => {
    const { startTime, endTime: endHour, dayOfWeek } = entry;
    const dayGroup = groupedEntries[dayOfWeek] || [];
    return {
      ...groupedEntries,
      [dayOfWeek]: [
        ...dayGroup,
        {
          startTime,
          endTime: endHour === '00:00' ? '24:00' : endHour,
        },
      ],
    };
  }, {});

// Create initial values
const createInitialValues = availabilityPlan => {
  const { timezone, entries } = availabilityPlan || {};
  const tz = timezone || defaultTimeZone();
  return {
    timezone: tz,
    ...createEntryDayGroups(entries),
  };
};

// Create entries from submit values
const createEntriesFromSubmitValues = values =>
  WEEKDAYS.reduce((allEntries, dayOfWeek) => {
    const dayValues = values[dayOfWeek] || [];
    const dayEntries = dayValues.map(dayValue => {
      const { startTime, endTime } = dayValue;
      // Note: This template doesn't support seats yet.
      return startTime && endTime
        ? {
          dayOfWeek,
          seats: 1,
          startTime,
          endTime: endTime === '24:00' ? '00:00' : endTime,
        }
        : null;
    });

    return allEntries.concat(dayEntries.filter(e => !!e));
  }, []);

// Create availabilityPlan from submit values
const createAvailabilityPlan = values => ({
  availabilityPlan: {
    type: 'availability-plan/time',
    timezone: values.timezone,
    entries: createEntriesFromSubmitValues(values),
  },
});

// Ensure that the AvailabilityExceptions are in sensible order.
//
// Note: if you allow fetching more than 100 exception,
// pagination kicks in and that makes client-side sorting impossible.
const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

//////////////////////////////////
// EditListingAvailabilityPanel //
//////////////////////////////////
const EditListingAvailabilityPanelComponent = props => {
  const {
    className,
    rootClassName,
    listing,
    availabilityExceptions,
    fetchExceptionsInProgress,
    onAddAvailabilityException,
    onDeleteAvailabilityException,
    disabled,
    ready,
    panelUpdated,
    history,
    onChange,
    onSubmit,
    onManageDisableScrolling,
    onNextTab,
    submitButtonText,
    updateInProgress,
    errors,
    params,
    form,
    userNativeLang,
  } = props;
  const formApi = useRef(null);

  const search = parse(location.search);
  const tab = (search && search.tab) || 'OneTimeTab';
  // Hooks
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [isEditExceptionsModalOpen, setIsEditExceptionsModalOpen] = useState(false);
  const [valuesFromLastSubmit, setValuesFromLastSubmit] = useState(null);
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const isNextButtonDisabled = !currentListing.attributes.availabilityPlan;
  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const defaultAvailabilityPlan = {
    type: 'availability-plan/time',
    timezone: defaultTimeZone(),
    entries: [
      // { dayOfWeek: 'mon', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'tue', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'wed', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'thu', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'fri', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sat', startTime: '09:00', endTime: '17:00', seats: 1 },
      // { dayOfWeek: 'sun', startTime: '09:00', endTime: '17:00', seats: 1 },
    ],
  };
  const availabilityPlan = currentListing.attributes.availabilityPlan || defaultAvailabilityPlan;
  const initialValues = valuesFromLastSubmit
    ? valuesFromLastSubmit
    : createInitialValues(availabilityPlan);

  const { publicData } = currentListing?.attributes;

  const listingScheduleTyp = publicData?.scheduleType;
  const savedCurrentTab =
    listingScheduleTyp !== undefined
      ? listingScheduleTyp === 'oneTime'
        ? 'OneTimeTab'
        : 'RecurringTab'
      : tab;
  const [currentTab, setCurrentTab] = useState(savedCurrentTab);
  const prevSelectedTab = useRef(savedCurrentTab);
  const selectedScheduleType = useRef(listingScheduleTyp);
  const onSelectCurrentTab = tabName => {
    prevSelectedTab.current = currentTab;
    selectedScheduleType.current =
      tabName === 'OneTimeTab' ? listingScheduleType.oneTime : listingScheduleType.recurring;
    setCurrentTab(tabName);
  };

  const { oneTimeScheduleList, recurringScheduleList, selectedDates, scheduleType } = publicData;

  const isPrevSelectedIsEqualCurrentTab = prevSelectedTab.current === currentTab;

  const initialselectedDates =
    publicData && publicData.selectedDates && isPrevSelectedIsEqualCurrentTab
      ? selectedDates.map(values => {
        const formattedDate = moment(values?.date).toDate();
        return { ...values, date: formattedDate };
      })
      : [
        {
          fieldName: ' ',
          date: null,
        },
      ];

  const initialOneTimeScheduleList =
    publicData &&
      scheduleType === 'oneTime' &&
      publicData.oneTimeScheduleList &&
      isPrevSelectedIsEqualCurrentTab
      ? oneTimeScheduleList.map(values => {
        const formattedDate = moment(values?.startDate?.date).toDate();

        let unpaidBreak = '';
        if (typeof values?.unpaidBreak === 'string') {
          unpaidBreak = UnpaidBreakOptions[0];
        } else {
          unpaidBreak = values?.unpaidBreak;
        }
        return { ...values, unpaidBreak, startDate: { date: formattedDate } };
      })
      : [{ startTime: {}, endTime: {}, startDate: null, unpaidBreak: UnpaidBreakOptions[0] }];

  const initialRecurringScheduleList =
    publicData &&
      scheduleType === 'recurring' &&
      publicData.recurringScheduleList &&
      isPrevSelectedIsEqualCurrentTab
      ? recurringScheduleList.map(values => {
        const formattedStartDate = values?.startDate?.date
          ? moment(values?.startDate?.date).toDate()
          : null;
        const formattedEndDate = moment(values?.endDate?.date).toDate();
        const recurringDays =
          values.recurringDays && typeof values.recurringDays === 'string'
            ? values.recurringDays.split(',')
            : [];
        let unpaidBreak = '';
        if (typeof values?.unpaidBreak === 'string') {
          unpaidBreak = UnpaidBreakOptions[0];
        } else {
          unpaidBreak = values?.unpaidBreak;
        }
        return {
          ...values,
          startDate: { date: formattedStartDate },
          endDate: { date: formattedEndDate },
          recurringDays,
          unpaidBreak,
        };
      })
      : [
        {
          startTime: {},
          endTime: {},
          startDate: { date: null },
          endDate: { date: null },
          unpaidBreak: UnpaidBreakOptions[0],
          recurringInterval: '',
          recurringDays: [],
        },
      ];
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const updatePrice = async () => {
    await axios
      .post(
        `${apiBaseUrl()}/api/stripe/createOrUpdatePaymentIntent`,
        {
          listingId: currentListing?.id?.uuid,
        },
        { withCredentials: true }
      )
      .then(res => {
        setShowInvoiceModal(true);
      })
      .catch(err => console.log(78888, err));
  };
  const calculateStartDate = (o, scheduleType) => {
    // Start time
    const startTimeHour = parseInt(o?.startTime?.hours?.value, 10);
    const startTimeMinute = parseInt(o?.startTime?.min?.value, 10);

    // End time
    const endTimeHour = parseInt(o?.endTime?.hours?.value);
    const endTimeMinute = parseInt(o?.endTime?.min?.value);

    const sDate = moment(o?.startDate?.date).set({
      hour: startTimeHour,
      minute: startTimeMinute,
    });

    let eDate =
      scheduleType === 'oneTime'
        ? moment(o?.startDate?.date).set({
          hour: endTimeHour,
          minute: endTimeMinute,
        })
        : moment(o?.endDate?.date).set({
          hour: endTimeHour,
          minute: endTimeMinute,
        });

    if (startTimeHour > endTimeHour) {
      eDate.add(1, 'day');
    }

    return sDate;
  };
  const calculateEndDate = (o, scheduleType) => {
    // Start time
    const startTimeHour = parseInt(o?.startTime?.hours?.value, 10);
    const startTimeMinute = parseInt(o?.startTime?.min?.value, 10);

    // End time
    const endTimeHour = parseInt(o?.endTime?.hours?.value);
    const endTimeMinute = parseInt(o?.endTime?.min?.value);

    let eDate =
      scheduleType === 'oneTime'
        ? moment(o?.startDate?.date).set({
          hour: endTimeHour,
          minute: endTimeMinute,
        })
        : moment(o?.endDate?.date).set({
          hour: endTimeHour,
          minute: endTimeMinute,
        });

    if (startTimeHour > endTimeHour) {
      eDate.add(1, 'day');
    }

    return eDate;
  };
  const handleSubmit = async values => {
    const { oneTimeScheduleList, recurringScheduleList, scheduleType, selectedDates } = values;

    const userOffset = moment().utcOffset();
    const oneTimeUpdatedData = oneTimeScheduleList.map(values => {

      const formattedDate =
        values?.startDate?.date
          ? moment(values?.startDate?.date).format('YYYY-MM-DDT00:00:00')
          : null;
      let unpaidBreak = '';
      if (typeof values?.unpaidBreak === 'string') {
        unpaidBreak = UnpaidBreakOptions[0];
      } else {
        unpaidBreak = values?.unpaidBreak;
      }
      return { ...values, unpaidBreak, startDate: { date: formattedDate } };
    });

    const recurringUpdatedData = recurringScheduleList.map(values => {
      const formattedStartDate = values?.startDate?.date
        ? moment(values?.startDate?.date).format('YYYY-MM-DDT00:00:00')
        : null;
      const formattedEndDate = values?.endDate?.date
        ? moment(values?.endDate?.date).format('YYYY-MM-DDT00:00:00')
        : null;

      const recurringDays =
        values.recurringDays && Array.isArray(values.recurringDays)
          ? values.recurringDays.join(',')
          : [];

      let unpaidBreak = '';
      if (typeof values?.unpaidBreak === 'string') {
        unpaidBreak = UnpaidBreakOptions[0];
      } else {
        unpaidBreak = values?.unpaidBreak;
      }
      return {
        ...values,
        startDate: { date: formattedStartDate },
        endDate: { date: formattedEndDate },
        recurringDays,
        unpaidBreak,
      };
    });

    const selectedDatesUpdatedData = selectedDates.map(values => {
      const formattedDate = values?.date
        ? moment(values?.date).format('YYYY-MM-DDTHH:mm:ss')
        : null;

      return { ...values, date: formattedDate };
    });
    const gigStartDate =
      scheduleType === 'oneTime' &&
        oneTimeScheduleList &&
        oneTimeScheduleList?.[0].startDate?.date !== null
        ? moment(oneTimeScheduleList?.map(o => calculateStartDate(o, scheduleType))?.[0]).format(
          'YYYY-MM-DDTHH:mm:ss'
        )
        : scheduleType === 'recurring' &&
          recurringScheduleList &&
          recurringScheduleList?.[0].startDate?.date !== null
          ? moment(recurringScheduleList?.map(o => calculateStartDate(o, scheduleType))?.[0]).format(
            'YYYY-MM-DDTHH:mm:ss'
          )
          : null;

    const gigEndDate =
      scheduleType === 'oneTime' &&
        oneTimeScheduleList &&
        oneTimeScheduleList?.[0].startDate?.date !== null
        ? moment(oneTimeScheduleList?.map(o => calculateEndDate(o, scheduleType))?.[0]).format(
          'YYYY-MM-DDTHH:mm:ss'
        )
        : scheduleType === 'recurring' &&
          recurringScheduleList &&
          recurringScheduleList?.[0].startDate?.date !== null
          ? moment(recurringScheduleList?.map(o => calculateEndDate(o, scheduleType))?.[0]).format(
            'YYYY-MM-DDTHH:mm:ss'
          )
          : null;

    const gigStartDateInNumberFormat =
      moment(gigStartDate)
        .add(userOffset, 'm')
        .valueOf() / 1000;
    const gigEndDateInNumberFormat =
      moment(gigEndDate)
        .add(userOffset, 'm')
        .valueOf() / 1000;

    let [djobStartDate, djobStartTime] = gigStartDate.split('T');
    let [djobEndDate, djobEndTime] = gigEndDate.split('T');
    djobStartTime = djobStartTime.slice(0, -3);
    djobEndTime = djobEndTime.slice(0, -3);

    let updatedValues = {};
    if (scheduleType === 'oneTime') {
      updatedValues = {
        publicData: {
          oneTimeScheduleList: oneTimeUpdatedData,
          recurringScheduleList: null,
          scheduleType,
          selectedDates: selectedDatesUpdatedData,
          gigStartDate,
          gigEndDate,
          gigEndDateInNumberFormat,
          gigStartDateInNumberFormat,
          djobStartDate,
          djobStartTime,
          djobEndDate,
          djobEndTime,
        },
      };
    } else if (scheduleType === 'recurring') {
      updatedValues = {
        publicData: {
          oneTimeScheduleList: null,
          recurringScheduleList: recurringUpdatedData,
          scheduleType,
          selectedDates: selectedDatesUpdatedData,
          gigStartDate,
          gigEndDate,
          gigEndDateInNumberFormat,
          gigStartDateInNumberFormat,
          djobStartDate,
          djobStartTime,
          djobEndDate,
          djobEndTime,
        },
      };
    }

    await onSubmit(updatedValues);

    if (currentListing?.attributes?.state === 'published') {
      await axios
        .patch(
          `${apiBaseUrl()}/api/timeStatement/updateListingDetails`,
          {
            listingId: currentListing?.id?.uuid,
            providerId: currentListing?.author?.id?.uuid,
          },
          { withCredentials: true }
        )
        .then(res => {
        })
        .catch(err => console.log(78888, err));
    }

    const arraysAreEqual = (arr1, arr2) => {
      if (arr1?.length !== arr2?.length) {
        return false;
      }

      for (let i = 0; i < arr1?.length; i++) {
        const item1 = arr1[i];
        const item2 = arr2[i];

        // Compare properties of each item
        if (JSON.stringify(item1) !== JSON.stringify(item2)) {
          return false;
        }
      }

      return true;
    };

    // Check if the array has changed
    const hasOneTimeScheduleChanged = !arraysAreEqual(
      publicData.oneTimeScheduleList,
      oneTimeUpdatedData
    );
    const hasRecurringScheduleChanged = !arraysAreEqual(
      publicData.recurringScheduleList,
      recurringUpdatedData
    );

    if (
      currentListing?.attributes?.state === 'published' &&
      (hasOneTimeScheduleChanged || hasRecurringScheduleChanged)
    ) {
      console.log('The array has changed.');
      updatePrice();
    }
  };

  const exceptionCount = availabilityExceptions ? availabilityExceptions.length : 0;
  const sortedAvailabilityExceptions = availabilityExceptions.sort(sortExceptionsByStartTime);

  // Save exception click handler
  const saveException = values => {
    const { availability, exceptionStartTime, exceptionEndTime } = values;

    // TODO: add proper seat handling
    const seats = availability === 'available' ? 1 : 0;

    return onAddAvailabilityException({
      listingId: listing.id,
      seats,
      start: timestampToDate(exceptionStartTime),
      end: timestampToDate(exceptionEndTime),
    })
      .then(() => {
        setIsEditExceptionsModalOpen(false);
      })
      .catch(e => {
      });
  };
  const tabs = [
    {
      text: <FormattedMessage id="EditListingAvailabilityPanel.OneTime" />,
      selected: tab === 'OneTimeTab',

      onClick: () => {
        history.replace(
          createResourceLocatorString(
            'EditListingPage',
            routeConfiguration(),
            { ...params },
            { tab: 'OneTimeTab' }
          )
        );
      },
    },

    {
      text: <FormattedMessage id="EditListingAvailabilityPanel.Recurring" />,
      selected: tab === 'RecurringTab',
      onClick: () => {
        history.replace(
          createResourceLocatorString(
            'EditListingPage',
            routeConfiguration(),
            { ...params },
            { tab: 'RecurringTab' }
          )
        );
      },
    },
  ];
  const tooltipDesc = <FormattedMessage id="EditListingAvailabilityPanel.schduleTooltipDesc" />;
  return (
    <main className={classes}>
      <HelpAndContact />
      <div className={css.editHeadingWrap}>
        <h1 className={css.title}>
          {isPublished ? (
            <FormattedMessage
              id="EditListingAvailabilityPanel.title"
              values={{
                listingTitle: (
                  <ListingLink listing={listing}>
                    <FormattedMessage id="EditListingAvailabilityPanel.listingTitle" />
                  </ListingLink>
                ),
              }}
            />
          ) : (
            <FormattedMessage id="EditListingDescriptionPanel.ListYourJob" />
          )}
        </h1>
        {currentListing?.id?.uuid && isPublished && (
          <div className={css.gotoBtn}>
            <NamedLink
              name="ListingPage"
              params={{
                id: currentListing?.id?.uuid,
                slug: createSlug(currentListing?.attributes?.title || ''),
              }}
            >
              <FormattedMessage id="EditListing.GoToListing" />
            </NamedLink>
          </div>
        )}
      </div>

      <section className={css.section}>
        <div className={css.tabLabelScheduling}>
          <FormattedMessage id="EditListingWizard.tabLabelScheduling" />
        </div>
        <div className={css.SubHeadingDesc}>
          <FormattedMessage id="EditListingAvailabilityPanel.SubHeadingDesc" />
        </div>


        <EditListingSchedulingForm
          className={css.form}
          initialValues={{
            oneTimeScheduleList: initialOneTimeScheduleList,
            recurringScheduleList: initialRecurringScheduleList,
            selectedDates: initialselectedDates,
            scheduleType: selectedScheduleType.current,
          }}
          saveActionMsg={submitButtonText}
          tab={currentTab}

          onChange={onChange}
          onSubmit={handleSubmit}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}

          formApi={formApi}
          userNativeLang={userNativeLang}

          setCurrentTab={onSelectCurrentTab}
          tooltipDesc={tooltipDesc}
          history={history}
        />
      </section>
      <Modal
        id="EditListingContactForm.agreeTermsConditions"
        isOpen={!!showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        containerClassName={css.invoiceModal}
      >
        <>
          <span className={css.invoiceTitle}>
            <FormattedMessage id="UpdatedInvoice.invoiceUpdated" />
          </span>
          {!!showInvoiceModal ? (
            <UpdatedInvoice
              listing={currentListing}
              tab={tab}
              setShowInvoiceModal={setShowInvoiceModal}
              userNativeLang={userNativeLang}
            />
          ) : null}
          <div className={css.ButtonContainer}>
            <Button
              type="button"
              className={css.cancelBtn}
              onClick={() => {
                setShowInvoiceModal(false);
              }}
            >
              <FormattedMessage id="UpdatedInvoice.OKAY" />
            </Button>
          </div>
        </>
      </Modal>

      {errors.showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingAvailabilityPanel.showListingFailed" />
        </p>
      ) : null}


      {onManageDisableScrolling ? (
        <Modal
          id="EditAvailabilityPlan"
          isOpen={isEditPlanModalOpen}
          onClose={() => setIsEditPlanModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          usePortal
        >
          <EditListingAvailabilityPlanForm
            formId="EditListingAvailabilityPlanForm"
            listingTitle={currentListing.attributes.title}
            availabilityPlan={availabilityPlan}
            weekdays={WEEKDAYS}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            inProgress={updateInProgress}
            fetchErrors={errors}
          />
        </Modal>
      ) : null}
      {onManageDisableScrolling ? (
        <Modal
          id="EditAvailabilityExceptions"
          isOpen={isEditExceptionsModalOpen}
          onClose={() => setIsEditExceptionsModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          usePortal
        >
          <EditListingAvailabilityExceptionForm
            formId="EditListingAvailabilityExceptionForm"
            onSubmit={saveException}
            timeZone={availabilityPlan.timezone}
            availabilityExceptions={sortedAvailabilityExceptions}
            updateInProgress={updateInProgress}
            fetchErrors={errors}
          />
        </Modal>
      ) : null}
    </main>
  );
};

EditListingAvailabilityPanelComponent.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
  availabilityExceptions: [],
};

EditListingAvailabilityPanelComponent.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  availabilityExceptions: arrayOf(propTypes.availabilityException),
  fetchExceptionsInProgress: bool.isRequired,
  onAddAvailabilityException: func.isRequired,
  onDeleteAvailabilityException: func.isRequired,
  onSubmit: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onNextTab: func.isRequired,
  submitButtonText: string.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

const EditListingAvailabilityPanel = compose(
  withRouter
  // injectIntl
)(EditListingAvailabilityPanelComponent);

export default EditListingAvailabilityPanel;
